import { create } from "zustand";
import { IProfile, ISetReminder, IUpdateProfile } from "../@types";
import { profileService } from "../services";
import i18n from "../i18n";
import {
  BUSINESS_TYPE_RETAIL,
  STORAGE_USER_DATA,
  SUBSCRIPTION,
  ROLE,
  BANK_ACCOUNTS,
  BUDGET,
  REMINDER,
} from "../constants";
import { convertImageToBase64 } from "../utils/convertToBase64";
import { fetchLogo } from "../utils";
import { BankData, Budget, BusinessType, UserDetail } from "../types";
import { subUserModule } from "../modules/subUserModule";
import { createBankAccount, updateProfile } from "../backend-services";
import { CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";

interface UserState {
  roles: ("Accountant" | "Admin" | "Operator")[];
  session: CognitoUserSession | null;
  cognitoUser: CognitoUser | null;
  setCognitoUser: (user: CognitoUser | null) => void;
  setSession: (session: CognitoUserSession | null) => void;
  user: any;
  users: UserDetail[];
  firstLogin: boolean;
  listInvoice: boolean;
  listInventory: boolean;
  setFirstLogin: (status: boolean) => void;
  setListInvoice: (status: boolean) => void;
  setListInventory: (status: boolean) => void;
  setRole: (role: any) => void;
  setUser: (user: any) => void;
  isPasswordChanged: boolean;
  updateUserPasswordChangedFlag: (
    userId: string,
    value: boolean,
  ) => Promise<void>;
  updateProfile: (profileData: Partial<IUpdateProfile>) => void;
}
export const useUserStore = create<UserState>((set, get) => {
  const parseJSON = (item: string | null | undefined, defaultValue: any) => {
    try {
      return item !== null && item !== undefined
        ? JSON.parse(item)
        : defaultValue;
    } catch (e) {
      console.error("Error parsing JSON from local storage:", e);
      return defaultValue;
    }
  };
  const roles = parseJSON(localStorage.getItem(ROLE) ?? null, []);

  return {
    user: {
      businessName: "",
      email: "",
      phone: "",
      address: "",
      id: "",
      firstName: "",
      lastName: "",
      brandLogo: null,
      logoUrl: "",
      state: "",
      city: "",
      code: "",
      industry: "",
      brandColor: "",
      bankAccounts: [],
    },
    firstLogin: true,
    listInvoice: true,
    listInventory: true,
    session: null,
    cognitoUser: null,
    roles,
    locale: localStorage.getItem("locale") || "en",
    brandColor: "#000000",
    users: [],
    isPasswordChanged: false,
    setUser: (user: any) => {
      localStorage.setItem(STORAGE_USER_DATA, JSON.stringify(user));
      set({ user });
    },

    setCognitoUser: (user: CognitoUser | null) => {
      set({ cognitoUser: user });
    },
    setSession: (session: CognitoUserSession | null) => {
      set({ session: session });
    },

    setFirstLogin: (status: boolean) => {
      const updatedUser = { ...get().user, firstLogin: status };
      localStorage.setItem(STORAGE_USER_DATA, JSON.stringify(updatedUser));
      set({ user: updatedUser, firstLogin: status });
    },
    setListInvoice: (status: boolean) => {
      set({ listInvoice: status });
    },
    setListInventory: (status: boolean) => {
      set({ listInventory: status });
    },

    isRetailBusiness: () => {
      const { user } = get();
      return user?.businessType === BUSINESS_TYPE_RETAIL;
    },

    setRole: (roles) => {
      localStorage.setItem(ROLE, JSON.stringify(roles));
      set({ roles });
    },

    updateBusinessType: async (type: BusinessType) => {
      try {
        await updateProfile({ businessType: type });
        const updatedUser = { ...get().user, businessType: type };
        set({ user: updatedUser });
      } catch (err) {
        console.error("Error updating business type:", err);
        throw err;
      }
    },

    updateUserPasswordChangedFlag: async (userId, value: boolean) => {
      try {
        await subUserModule.updateUser(userId, { firstPasswordChanged: value });
        const { user } = get();
        if (user.id === userId) {
          const updatedUser = { ...user, firstPasswordChanged: value };
          set({
            user: updatedUser,
            isPasswordChanged: value,
          });
          localStorage.setItem(STORAGE_USER_DATA, JSON.stringify(updatedUser));
        }
      } catch (error) {
        throw new Error("Error updating firstPasswordChanged flag");
      }
    },
    updateProfile: async (profileData: Partial<IUpdateProfile>) => {
      try {
        await updateProfile(profileData);
        const updatedUser = { ...get().user, ...profileData };
        set({ user: updatedUser });
      } catch (err) {
        console.error("Error updating business type:", err);
        throw err;
      }
    },
    createBankAccount: async (data: BankData) => {
      try {
        const result = await createBankAccount(data);
        const currentUser = get().user;
        const updatedUser = {
          ...currentUser,
          bankAccounts: [...(currentUser.bankAccounts || []), ...result.data],
        };
        set({ user: updatedUser });
      } catch (err) {
        console.error("Error creating bank account:", err);
        throw err;
      }
    },
  };
});
